import { SecurityClearance } from "@fairfleet/geotab";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { normalizeSearchText } from "@/utilities/text/normalizeSearchText";
import { getSecurityIdentifierLabel } from "@/utilities/user/securityIdentifierLabel";

type SecurityFilters = NonNullable<SecurityClearance["securityFilters"]>;

interface ClearanceFiltersProps {
  filters: SecurityFilters;
}

export function ClearanceFilters({ filters }: ClearanceFiltersProps) {
  const { t } = useTranslation();
  const [featureSearch, setFeatureSearch] = useState("");

  const labelledFilters = useMemo(
    () =>
      filters
        .filter(filter => !!filter.securityIdentifier)
        .map(filter => ({
          ...filter,
          label: getSecurityIdentifierLabel(filter.securityIdentifier as string, t),
        })),
    [filters, t],
  );

  const visibleFilters = useMemo(() => {
    const query = normalizeSearchText(featureSearch);
    if (!query) return labelledFilters;
    return labelledFilters.filter(
      filter =>
        normalizeSearchText(filter.securityIdentifier).includes(query) ||
        normalizeSearchText(filter.label).includes(query),
    );
  }, [featureSearch, labelledFilters]);

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2">{t("clearances.filters.title")}</Typography>

        <TextField
          label={t("clearances.filters.search")}
          value={featureSearch}
          onChange={event => setFeatureSearch(event.target.value)}
          fullWidth
          size="small"
        />

        <Chip size="small" label={t("clearances.filters.count", { total: visibleFilters.length })} />

        <Stack spacing={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          {visibleFilters.map(filter => {
            const isDenied = filter.isAdd === false;
            return (
              <Stack
                key={`${filter.securityIdentifier}:${filter.isAdd ? "add" : "remove"}`}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  px: 1,
                  py: 0.75,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-of-type": { borderBottom: "none" },
                }}
              >
                <Chip
                  size="small"
                  color={isDenied ? "error" : "primary"}
                  icon={isDenied ? <CloseIcon /> : <CheckIcon />}
                  label=""
                  sx={{ minWidth: 28, "& .MuiChip-label": { px: 0 } }}
                />
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                    {filter.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                    {filter.securityIdentifier}
                  </Typography>
                </Stack>
              </Stack>
            );
          })}

          {visibleFilters.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
              {t("clearances.filters.noResults")}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}
